import { useEffect, useMemo, useState } from 'react'
import { Building2, Pencil, Plus, ShieldCheck, Users } from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import DatePicker from '../../components/DatePicker'
import { FormCols } from '../../components/Form'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import StatStrip from '../../components/StatStrip'
import Select from '../../components/Select'
import { useToast } from '../../components/toastContext'
import TextField from '../../components/TextField'
import { listLicenses, saveLicense } from './organisationsService'
import { licenseManagementStrings as S } from './strings'
import { licenseInformationStrings as L } from './strings'
import './organisations.css'
import { serverDetail } from '../../lib/i18n'

const BLANK = {
  id: null,
  companyId: '',
  passKey: '',
  startDate: '',
  licenseType: '1',
  usersNumber: '1',
  duration: '1',
}

/** إدارة رخصة الاستخدام — Views/LicenseManagement/Index.cshtml. */
export default function LicenseManagementPage() {
  const toast = useToast()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    listLicenses().then((response) => {
      setData(response)
      setLoading(false)
    })
  }

  useEffect(load, [])

  const set = (key) => (value) => setForm((previous) => ({ ...previous, [key]: value }))

  /** setLicenseProp — the type decides which numeric fields are editable. */
  const enabled = S.typeFields[form?.licenseType] ?? { users: false, duration: false }

  const changeType = (value) => {
    const rule = S.typeFields[value] ?? { users: false, duration: false }
    setForm((previous) => ({
      ...previous,
      licenseType: value,
      usersNumber: rule.users ? previous.usersNumber : '1',
      duration: rule.duration ? previous.duration : '1',
    }))
  }

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    try {
      const response = await saveLicense(form)
      setSaving(false)
      if (!response.ok) return toast.error(serverDetail(response.message) || S.saveUnavailable)

      setForm(null)
      toast.success(S.saved)
      load()
    } catch (error) {
      // This legacy all-company licence book has no save endpoint. The service
      // refuses explicitly; keep the form open and tell the operator instead of
      // allowing an unhandled rejection or claiming that anything was saved.
      setSaving(false)
      toast.error(serverDetail(error?.message) || S.saveUnavailable)
    }
  }

  /** Licence type id → its name. The table printed the raw id — a column of
      `5`, `4`, `3`, `1` — while the edit form beside it had the names all
      along. */
  const typeNames = useMemo(
    () => new Map((data?.licenseTypes ?? []).map((type) => [String(type.id), type.name])),
    [data],
  )

  const columns = useMemo(
    () => [
      {
        key: 'companyName',
        header: S.columns.company,
        render: (row) => <bdi>{row.companyName}</bdi>,
      },
      {
        key: 'licenseType',
        header: S.columns.licenseType,
        value: (row) => typeNames.get(String(row.licenseType)) ?? '',
        render: (row) => (
          <span className="org-type" dir="ltr">
            {typeNames.get(String(row.licenseType)) ?? L.unavailable}
          </span>
        ),
      },
      {
        key: 'usersNumber',
        header: S.columns.usersNumber,
        align: 'center',
        render: (row) => (
          <span className="org-num">
            {row.usersNumber == null ? L.unavailable : row.usersNumber.toLocaleString('en-US')}
          </span>
        ),
      },
      {
        key: 'startDate',
        header: S.columns.startDate,
        render: (row) => <span className="org-muted">{row.startDate ?? L.unavailable}</span>,
      },
      {
        key: 'duration',
        header: S.columns.duration,
        align: 'center',
        render: (row) => (
          <span className="org-num">
            {row.duration == null ? L.unavailable : row.duration.toLocaleString('en-US')}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '72px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton
            icon={Pencil}
            label={S.edit}
            onClick={() =>
              setForm({
                ...BLANK,
                id: row.id,
                companyId: row.companyId,
                licenseType: String(row.licenseType ?? '1'),
                usersNumber: String(row.usersNumber ?? '1'),
                duration: String(row.duration ?? '1'),
                startDate: row.startDate ?? '',
              })
            }
          />
        ),
      },
    ],
    [typeNames],
  )

  /** What the licence book actually holds — clients, seats sold, and how many
      of those licences are live today. Summed off the rows already loaded. */
  const summary = useMemo(() => {
    const rows = data?.rows ?? []
    const seats = rows.reduce((total, row) => total + (row.usersNumber ?? 0), 0)
    const live = rows.filter((row) => {
      if (!row.startDate || row.duration == null) return false
      const ends = new Date(`${row.startDate}T00:00:00`).getTime() + row.duration * 86400000
      return ends >= Date.now()
    }).length
    return [
      { id: 'clients', icon: Building2, label: S.summary.clients, value: rows.length },
      { id: 'seats', icon: Users, label: S.summary.seats, value: seats },
      { id: 'live', icon: ShieldCheck, label: S.summary.live, value: live, tone: 'success' },
    ]
  }, [data])

  const companyOptions = useMemo(
    () => (data?.companies ?? []).map((company) => ({ value: company.id, label: company.arabicName })),
    [data],
  )

  const typeOptions = useMemo(
    () => (data?.licenseTypes ?? []).map((type) => ({ value: String(type.id), label: type.name })),
    [data],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        actions={
          <Button onClick={() => setForm({ ...BLANK, companyId: data?.companies?.[0]?.id ?? '' })}>
            <Plus size={16} strokeWidth={1.75} />
            {S.create}
          </Button>
        }
      />

      <StatStrip items={summary} label={S.panel} loading={loading} />

      <DataTable
        columns={columns}
        rows={data?.rows ?? []}
        loading={loading}
        title={S.panel}
        icon={ShieldCheck}
        emptyMessage={S.emptyMessage}
        getRowId={(row) => row.id}
      />

      <Modal
        open={!!form}
        title={S.panel}
        size="lg"
        icon={ShieldCheck}
        onClose={() => setForm(null)}
        footer={
          <>
            <Button type="submit" form="license-form" disabled={saving}>
              {saving ? '…' : S.save}
            </Button>
            <Button variant="ghost" onClick={() => setForm(null)}>
              {S.close}
            </Button>
          </>
        }
      >
        {form && (
          <form id="license-form" onSubmit={submit}>
            <FormCols>
              <FormRow label={S.form.company}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.companyId}
                    onChange={set('companyId')}
                    options={companyOptions}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.licenseType}>
                {(id) => (
                  <Select
                    id={id}
                    value={form.licenseType}
                    onChange={changeType}
                    options={typeOptions}
                  />
                )}
              </FormRow>

              <FormRow className="form-span" label={S.form.passKey}>
                {(id) => (
                  <TextField
                    id={id}
                    value={form.passKey}
                    onChange={set('passKey')}
                    autoComplete="new-password"
                    dir="ltr"
                  />
                )}
              </FormRow>

              <FormRow
                label={S.form.usersNumber}
                hint={enabled.users ? S.form.minMaxUsers : undefined}
              >
                {(id) => (
                  <TextField
                    id={id}
                    type="number"
                    min="1"
                    max="9999999"
                    value={form.usersNumber}
                    onChange={set('usersNumber')}
                    disabled={!enabled.users}
                    dir="ltr"
                  />
                )}
              </FormRow>

              <FormRow
                label={S.form.duration}
                hint={enabled.duration ? S.form.minMaxDuration : undefined}
              >
                {(id) => (
                  <TextField
                    id={id}
                    type="number"
                    min="1"
                    max="9999999"
                    value={form.duration}
                    onChange={set('duration')}
                    disabled={!enabled.duration}
                    dir="ltr"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.startDate}>
                {(id) => (
                  <DatePicker id={id} value={form.startDate} onChange={set('startDate')} />
                )}
              </FormRow>
            </FormCols>
          </form>
        )}
      </Modal>

    </>
  )
}
